import React, { useEffect, useRef, useState } from "react";
import { processSteps } from "./homeData";

const cardThemes = [
  {
    card: "#ffffff",
    text: "#111111",
    muted: "rgba(17,17,17,0.72)",
    badge: "rgba(237,134,15,0.12)",
    badgeText: "#ed860f",
    border: "1px solid rgba(237,134,15,0.22)",
  },
  {
    card: "#ed860f",
    text: "#111111",
    muted: "rgba(17,17,17,0.78)",
    badge: "rgba(255,255,255,0.88)",
    badgeText: "#ed860f",
    border: "1px solid rgba(237,134,15,0.35)",
  },
  {
    card: "#000000",
    text: "#ffffff",
    muted: "rgba(255,255,255,0.82)",
    badge: "rgba(237,134,15,0.18)",
    badgeText: "#ed860f",
    border: "1px solid rgba(237,134,15,0.28)",
  },
];

function HowItWorksSection({
  title = "How It Works",
  subtitle = "Our comprehensive implementation process ensures a smooth transition to smarter fleet management with minimal disruption to your operations.",
  steps: customSteps,
}) {
  const steps = customSteps?.length ? customSteps : processSteps || [];

  const cardRefs = useRef([]);
  const frameRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const updateActiveCard = () => {
      if (!cardRefs.current.length) return;

      const targetTop = window.innerWidth >= 768 ? 140 : 100;

      let closestIndex = 0;
      let closestDistance = Number.POSITIVE_INFINITY;

      cardRefs.current.forEach((card, index) => {
        if (!card) return;

        const rect = card.getBoundingClientRect();
        const distance = Math.abs(rect.top - targetTop);

        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      setActiveIndex(closestIndex);
    };

    const handleScroll = () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
      frameRef.current = requestAnimationFrame(updateActiveCard);
    };

    updateActiveCard();

    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll);

    return () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, [steps.length]);

  if (!steps.length) return null;

  const progressPercent =
    steps.length > 1 ? (activeIndex / (steps.length - 1)) * 100 : 0;

  return (
    <section className="bg-[#faf7f3] px-4 py-14 sm:px-6 lg:px-8 lg:py-16">
      <div className="mx-auto max-w-[1450px]">
        <div className="mx-auto max-w-[760px] text-center">
          <h2 className="text-[30px] font-extrabold leading-tight text-[#111111] sm:text-[38px]">
            {title}
          </h2>

          <p className="mx-auto mt-4 max-w-[650px] text-[13px] leading-[1.7] text-[#4e4741] sm:text-[14px]">
            {subtitle}
          </p>
        </div>

        <div className="relative mt-12 flex items-start gap-6 lg:gap-10">
          <div className="sticky top-[140px] hidden w-[38px] shrink-0 justify-center md:flex">
            <div className="relative h-[180px] w-[18px] overflow-hidden rounded-full bg-[#d9d9d9]">
              <div
                className="absolute left-1/2 top-0 w-[7px] rounded-full bg-[#d88934] transition-all duration-500 ease-out"
                style={{
                  height: `${Math.max(28, progressPercent * 1.52)}px`,
                  transform: "translateX(-50%)",
                }}
              />
            </div>
          </div>

          <div className="w-full">
            <div className="relative mx-auto w-full max-w-[1280px] pb-8 md:pb-[12vh]">
              {steps.map((step, index) => {
                const isActive = index === activeIndex;
                const isPassed = index < activeIndex;
                const theme = cardThemes[index % cardThemes.length];

                return (
                  <article
                    key={`${step.step}-${step.title}`}
                    ref={(node) => {
                      cardRefs.current[index] = node;
                    }}
                    className={`sticky mb-10 min-h-[230px] rounded-[14px] px-5 py-8 transition-[transform,box-shadow,opacity,filter] duration-500 ease-out last:mb-0 sm:min-h-[245px] sm:px-10 sm:py-10 md:mb-[14vh] md:min-h-[260px] md:px-16 md:py-12 lg:px-24 ${
                      isActive
                        ? "scale-[1.025] shadow-2xl opacity-100 blur-0"
                        : isPassed
                          ? "scale-[0.97] shadow-md opacity-95 blur-0"
                          : "scale-[0.985] shadow-sm opacity-100 blur-0"
                    }`}
                    style={{
                      top: `${95 + index * 14}px`,
                      zIndex: 20 + index,
                      transformOrigin: "center top",
                      background: theme.card,
                      color: theme.text,
                      border: theme.border,
                    }}
                  >
                    <div
                      className="mb-5 inline-flex h-10 min-w-10 items-center justify-center rounded-full px-3 text-sm font-extrabold"
                      style={{
                        backgroundColor: theme.badge,
                        color: theme.badgeText,
                      }}
                    >
                      {step.step}
                    </div>

                    <h3 className="text-[21px] font-extrabold leading-tight sm:text-[24px]">
                      {step.title}
                    </h3>

                    <p
                      className="mt-5 max-w-[900px] text-[13px] leading-[1.7] sm:text-[14px] md:text-[15px]"
                      style={{
                        color: theme.muted,
                      }}
                    >
                      {step.text}
                    </p>
                  </article>
                );
              })}
            </div>

            <div className="mt-8 flex justify-center gap-2 md:hidden">
              {steps.map((step, index) => (
                <span
                  key={step.step}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    index === activeIndex
                      ? "w-8 bg-[#d88934]"
                      : "w-2 bg-[#d9d9d9]"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default HowItWorksSection;